import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Gift, Plus, ExternalLink, Sparkles, Trash2, ShoppingCart, ArrowLeft, Share2, Mic } from "lucide-react";

const STATUS = { idea: "Idée", reserved: "Réservé", bought: "Acheté" };

export default function GiftLists() {
  const [lists, setLists] = useState([]);
  const [sel, setSel] = useState(null);
  const [nl, setNl] = useState({ title: "", occasion: "", recipient: "" });
  const [nlOpen, setNlOpen] = useState(false);
  const [gi, setGi] = useState({ name: "", price: "", url: "", store: "", note: "" });
  const [sug, setSug] = useState({ recipient: "", occasion: "", budget: "", interests: "" });
  const [ideas, setIdeas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [phrase, setPhrase] = useState("");
  const [pBusy, setPBusy] = useState(false);

  const loadLists = () => api.get("/gifts/lists").then((r) => setLists(r.data));
  useEffect(() => { loadLists(); }, []);

  const openList = async (lid) => { const { data } = await api.get(`/gifts/lists/${lid}`); setSel(data); };

  const createList = async () => {
    if (!nl.title.trim()) return toast.error("Titre requis");
    const { data } = await api.post("/gifts/lists", nl);
    setNl({ title: "", occasion: "", recipient: "" }); setNlOpen(false);
    toast.success("Liste créée"); await loadLists(); openList(data.id);
  };

  const delList = async (lid) => { await api.delete(`/gifts/lists/${lid}`); setSel(null); toast.success("Liste supprimée"); loadLists(); };

  const addItem = async (item) => {
    const payload = item || { ...gi, price: gi.price ? parseFloat(gi.price) : null };
    if (!payload.name?.trim()) return toast.error("Nom requis");
    await api.post(`/gifts/lists/${sel.list.id}/items`, payload);
    if (!item) setGi({ name: "", price: "", url: "", store: "", note: "" });
    toast.success("Cadeau ajouté"); openList(sel.list.id); loadLists();
  };

  const setStatus = async (iid, status) => { await api.patch(`/gifts/items/${iid}`, { status }); openList(sel.list.id); };
  const delItem = async (iid) => { await api.delete(`/gifts/items/${iid}`); openList(sel.list.id); loadLists(); };

  const suggest = async () => {
    setBusy(true); setIdeas([]);
    try {
      const { data } = await api.post("/gifts/suggest", sug);
      setIdeas(data.ideas || []);
      if (!data.ideas?.length) toast("Aucune idée trouvée, précise ta demande");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const shareList = async (lid) => {
    const { data } = await api.post(`/gifts/lists/${lid}/share`);
    const url = `${window.location.origin}/api/share/gifts/${data.share_token}`;
    setShareUrl(url);
    try { await navigator.clipboard.writeText(url); } catch (e) {}
    toast.success("Lien de partage copié");
  };

  const fromText = async () => {
    if (!phrase.trim()) return toast.error("Dis ou écris une phrase");
    setPBusy(true);
    try {
      const { data } = await api.post("/gifts/from-text", { text: phrase.trim() });
      toast.success(`Liste « ${data.title} » créée (${data.item_count} idée(s))`);
      setPhrase(""); await loadLists(); openList(data.list_id);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setPBusy(false); }
  };

  const micPhrase = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("Reconnaissance vocale non supportée");
    const rec = new SR(); rec.lang = "fr-FR"; rec.interimResults = true;
    rec.onresult = (e) => { let t = ""; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript; setPhrase(t); };
    try { rec.start(); toast("OMY vous écoute…"); } catch (e) {}
  };

  if (sel) {
    const { list, items } = sel;
    return (
      <div className="space-y-6">
        <button data-testid="gift-back-btn" onClick={() => { setSel(null); setIdeas([]); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Toutes mes listes</button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-primary">Liste de cadeaux{list.occasion ? ` · ${list.occasion}` : ""}</span>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-1">{list.title}</h1>
            {list.recipient && <p className="text-sm text-muted-foreground mt-1">Pour {list.recipient}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="gift-share-btn" variant="outline" onClick={() => shareList(list.id)} className="rounded-full"><Share2 size={15} className="mr-1.5" /> Partager</Button>
            <Button data-testid="gift-list-delete" variant="ghost" onClick={() => delList(list.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></Button>
          </div>
        </div>
        {shareUrl && (
          <div data-testid="share-url" className="rounded-xl bg-primary/10 border border-primary/30 px-4 py-2 text-xs text-primary break-all">
            Lien de partage (copié) : {shareUrl}
          </div>
        )}

        <div className="space-y-2">
          {items.length === 0 && <p className="text-sm text-muted-foreground">Aucun cadeau pour l'instant. Ajoute-en un ou demande des idées à OMY.</p>}
          {items.map((it) => (
            <div key={it.id} data-testid={`gift-item-${it.id}`} className="omy-glass rounded-2xl p-4 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{it.name}</p>
                <p className="text-xs text-muted-foreground truncate">{[it.reserved_by ? `Réservé par ${it.reserved_by}` : null, it.store, it.price != null ? `${it.price} €` : null, it.note].filter(Boolean).join(" · ") || "—"}</p>
              </div>
              <select data-testid={`gift-status-${it.id}`} value={it.status} onChange={(e) => setStatus(it.id, e.target.value)}
                className="text-xs bg-black/30 border border-white/10 rounded-full px-2 py-1 outline-none shrink-0">
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              {it.url && <a href={it.url} target="_blank" rel="noreferrer" data-testid={`gift-order-${it.id}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline shrink-0"><ShoppingCart size={13} /> Commander</a>}
              <button data-testid={`gift-del-${it.id}`} onClick={() => delItem(it.id)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>

        <div className="omy-glass rounded-2xl p-5 space-y-3">
          <h3 className="text-sm font-medium flex items-center gap-2"><Plus size={16} className="text-primary" /> Ajouter un cadeau</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input data-testid="gift-name-input" value={gi.name} onChange={(e) => setGi({ ...gi, name: e.target.value })} className="bg-black/30" placeholder="Nom du cadeau" />
            <Input data-testid="gift-url-input" value={gi.url} onChange={(e) => setGi({ ...gi, url: e.target.value })} className="bg-black/30" placeholder="Lien d'achat (optionnel)" />
            <Input data-testid="gift-price-input" type="number" value={gi.price} onChange={(e) => setGi({ ...gi, price: e.target.value })} className="bg-black/30" placeholder="Prix (€)" />
            <Input data-testid="gift-store-input" value={gi.store} onChange={(e) => setGi({ ...gi, store: e.target.value })} className="bg-black/30" placeholder="Boutique" />
          </div>
          <Button data-testid="gift-add-btn" onClick={() => addItem()} className="bg-primary hover:bg-primary/90 rounded-full">Ajouter</Button>
        </div>

        <div className="omy-glass rounded-2xl p-5 space-y-3">
          <h3 className="text-sm font-medium flex items-center gap-2"><Sparkles size={16} className="text-primary" /> Idées cadeaux par OMY</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input data-testid="suggest-recipient" value={sug.recipient} onChange={(e) => setSug({ ...sug, recipient: e.target.value })} className="bg-black/30" placeholder="Pour qui ? (ex. ma mère, 60 ans)" />
            <Input data-testid="suggest-occasion" value={sug.occasion} onChange={(e) => setSug({ ...sug, occasion: e.target.value })} className="bg-black/30" placeholder="Occasion (Noël, anniversaire…)" />
            <Input data-testid="suggest-budget" value={sug.budget} onChange={(e) => setSug({ ...sug, budget: e.target.value })} className="bg-black/30" placeholder="Budget (ex. 50€)" />
            <Input data-testid="suggest-interests" value={sug.interests} onChange={(e) => setSug({ ...sug, interests: e.target.value })} className="bg-black/30" placeholder="Centres d'intérêt" />
          </div>
          <Button data-testid="suggest-btn" onClick={suggest} disabled={busy} className="bg-primary hover:bg-primary/90 rounded-full">{busy ? "OMY cherche…" : "Trouver des idées"}</Button>
          {ideas.length > 0 && (
            <div className="space-y-2 pt-1" data-testid="suggest-results">
              {ideas.map((idea, i) => (
                <div key={i} data-testid={`suggest-idea-${i}`} className="rounded-xl bg-black/20 p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{idea.name}{idea.price != null ? ` · ~${idea.price}€` : ""}</p>
                    {idea.description && <p className="text-xs text-muted-foreground">{idea.description}</p>}
                  </div>
                  <a href={idea.url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0"><ExternalLink size={12} /> Voir</a>
                  <Button data-testid={`suggest-add-${i}`} onClick={() => addItem({ name: idea.name, price: idea.price, url: idea.url, store: idea.store, note: idea.description })}
                    size="sm" variant="outline" className="rounded-full shrink-0">Ajouter</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Listes de cadeaux</h1>
          <p className="text-muted-foreground text-sm mt-1">Crée des listes, garde les liens prêts à commander, et laisse OMY te souffler des idées.</p>
        </div>
        <Dialog open={nlOpen} onOpenChange={setNlOpen}>
          <DialogTrigger asChild><Button data-testid="new-list-btn" className="bg-primary hover:bg-primary/90 rounded-full"><Plus size={16} className="mr-1.5" /> Nouvelle liste</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvelle liste de cadeaux</DialogTitle><DialogDescription>Ex. « Noël 2026 », « Anniversaire de Léa ».</DialogDescription></DialogHeader>
            <div className="space-y-3 pt-1">
              <div><Label>Titre</Label><Input data-testid="new-list-title" value={nl.title} onChange={(e) => setNl({ ...nl, title: e.target.value })} className="mt-1 bg-black/30" placeholder="Noël 2026" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Occasion</Label><Input data-testid="new-list-occasion" value={nl.occasion} onChange={(e) => setNl({ ...nl, occasion: e.target.value })} className="mt-1 bg-black/30" placeholder="Noël" /></div>
                <div><Label>Pour qui</Label><Input data-testid="new-list-recipient" value={nl.recipient} onChange={(e) => setNl({ ...nl, recipient: e.target.value })} className="mt-1 bg-black/30" placeholder="La famille" /></div>
              </div>
              <Button data-testid="new-list-create" onClick={createList} className="bg-primary hover:bg-primary/90 rounded-full w-full">Créer la liste</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="omy-glass rounded-2xl p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <Sparkles size={18} className="text-primary shrink-0 hidden sm:block" />
        <Input data-testid="phrase-input" value={phrase} onChange={(e) => setPhrase(e.target.value)} className="bg-black/30 flex-1"
          placeholder="Dis à OMY : « Crée une liste Noël pour les enfants avec un vélo et des Lego »" />
        <div className="flex gap-2">
          <button data-testid="phrase-mic" onClick={micPhrase} title="Dicter" className="w-10 h-10 rounded-full bg-white/5 text-primary flex items-center justify-center shrink-0 hover:bg-white/10"><Mic size={16} /></button>
          <Button data-testid="phrase-create" onClick={fromText} disabled={pBusy} className="bg-primary hover:bg-primary/90 rounded-full">{pBusy ? "OMY crée…" : "Créer"}</Button>
        </div>
      </div>

      {lists.length === 0 ? (
        <div className="omy-glass rounded-3xl p-10 text-center">
          <Gift size={40} className="mx-auto text-primary mb-3" />
          <p className="text-muted-foreground">Aucune liste pour l'instant. Crée ta première liste de cadeaux.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {lists.map((l) => (
            <button key={l.id} data-testid={`gift-list-${l.id}`} onClick={() => openList(l.id)}
              className="omy-glass rounded-2xl p-5 text-left hover:border-primary/40 transition-colors">
              <Gift size={20} className="text-primary mb-3" />
              <p className="font-display font-medium">{l.title}</p>
              <p className="text-xs text-muted-foreground mt-1">{[l.occasion, l.recipient].filter(Boolean).join(" · ") || "—"}</p>
              <p className="text-xs text-primary mt-3">{l.item_count} cadeau(x)</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
